import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Maximize from "@mui/icons-material/Maximize";
import Remove from "@mui/icons-material/Remove";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { MinimizableDialog } from "components/MinimizableDialog";
import { JobStatus } from "generated-graphql/graphql";
import { terminalJobStatuses } from "hooks/useLongPollingForJob";
import { useLongPollingForJobWithFileOutput } from "hooks/useLongPollingForJobWithFileOutput";
import { DialogMode, useMinimizedDialogs } from "providers/minimizedDialogs";
import { ReactNode, useCallback, useEffect, useState } from "react";

type ExportPhase = "running" | "success" | "timeout" | "error";

export type JobExportProgressDialogProps = {
  /** Dialog id in the minimized-dialogs registry; also used to target
   * minimize/expand/close actions for this specific instance. */
  id: string;
  jobId: string;
  /** Subject of the export, e.g. "Manifest PDFs" or "Discrepancy export" —
   * plugged into the phase-specific headline below. */
  title: string;
  /** Body copy shown under the progress bar while the job is running.
   * Defaults to copy generic enough for any export type. */
  runningDescription?: string;
  /** Short description of the export's filters (e.g. a shipped-date range),
   * shown alongside the title so multiple tray entries stay distinguishable. */
  label?: string;
  /** Whether this export's job also emails the user download links on
   * completion (the manifest-PDF deliverables job does; plain EXPORT_DATA
   * spreadsheet jobs do not). Drives the success/timeout copy so we never
   * promise an email that isn't coming. */
  notifiesByEmail?: boolean;
  open: boolean;
  mode?: DialogMode;
  trayIndex?: number;
  onClose:
    | (() => void)
    | ((
        event: React.MouseEvent<HTMLElement>,
        reason: "backdropClick" | "escapeKeyDown" | "cancelButtonClick" | "closeButtonClick"
      ) => void);
};

function titleForPhase(title: string, phase: ExportPhase, label?: string): string {
  const suffix = label ? ` — ${label}` : "";
  switch (phase) {
    case "running":
      return `${title}${suffix}…`;
    case "success":
      return `${title} ready${suffix}`;
    case "timeout":
      return `Still preparing ${title}${suffix}`;
    case "error":
      return `${title} failed${suffix}`;
  }
}

function primaryActionButton(
  phase: ExportPhase,
  onMinimize: () => void,
  onClose: () => void,
  onCheckAgain: () => void
): ReactNode {
  if (phase === "running") {
    return (
      <Button onClick={onMinimize} variant="text" size="medium">
        Minimize
      </Button>
    );
  }
  if (phase === "timeout") {
    // The poll has stopped but the job may still finish — offer a manual
    // re-check alongside dismissal so the file stays discoverable even when
    // no completion email is coming.
    return (
      <Stack direction="row" spacing={1}>
        <Button onClick={onCheckAgain} variant="text" size="medium">
          Check again
        </Button>
        <Button onClick={onClose} variant="text" size="medium">
          Close
        </Button>
      </Stack>
    );
  }
  return (
    <Button onClick={onClose} variant="text" size="medium">
      Close
    </Button>
  );
}

function PhaseIndicator({ phase }: { phase: ExportPhase }) {
  if (phase === "running") {
    return <CircularProgress size={16} />;
  }
  if (phase === "error") {
    return <ErrorOutlineIcon fontSize="small" color="error" />;
  }
  if (phase === "timeout") {
    return <CheckIcon fontSize="small" color="info" />;
  }
  return <CheckIcon fontSize="small" color="success" />;
}

/**
 * Progress/success/error view for an export job (manifest PDFs, manifest
 * CSV, discrepancy CSV, …), rendered by the global minimized-dialogs tray
 * (see providers/minimizedDialogs.tsx and
 * components/MinimizedDialogs/registry.tsx). Mirrors
 * components/Document/DocumentAIAnalyzingDialog.tsx: it owns the job polling
 * itself so the export keeps running while the user navigates away, and
 * exposes the same minimize/restore title-bar affordance.
 */
export function JobExportProgressDialog({
  id,
  jobId,
  title,
  runningDescription = "Preparing your export…",
  label,
  notifiesByEmail = false,
  open,
  mode = "centered",
  trayIndex,
  onClose,
}: JobExportProgressDialogProps) {
  const { minimize, expand, close } = useMinimizedDialogs();
  const [phase, setPhase] = useState<ExportPhase>("running");
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const handleUrlsReady = useCallback((urls: string[]) => {
    for (const url of urls) {
      window.open(url, "_blank");
    }
    setPhase("success");
  }, []);

  const handleError = useCallback((err: Error) => {
    setErrorMessage(err.message);
    setPhase("error");
  }, []);

  const { job, pollingTimedOut, startPolling } = useLongPollingForJobWithFileOutput({
    timeoutInSeconds: 1200,
    onUrlsReady: handleUrlsReady,
    onError: handleError,
  });

  useEffect(() => {
    startPolling(jobId);
    // oxlint-disable-next-line exhaustive-deps -- start exactly once per jobId; startPolling identity is stable
  }, [jobId]);

  // SOFT success: the export job runs server-side regardless of whether the
  // client is still around to poll for it. So a client-side polling timeout
  // should not read as a failure — show a "still working" view rather than
  // an error (with copy that only promises an email when one is coming).
  useEffect(() => {
    if (pollingTimedOut) {
      setPhase((current) => (current === "running" ? "timeout" : current));
    }
  }, [pollingTimedOut]);

  // A job that terminates in any non-success state (FAILED, CANCELLED,
  // FAILED_NEEDS_HUMAN, …) stops the poll without firing onUrlsReady or
  // onError — without this, the tray would spin forever on a failed export.
  // pollingTimedOut is excluded because the polling hook fabricates a
  // Failed-status job on timeout, which the effect above already handles as
  // a soft success.
  useEffect(() => {
    if (!job || pollingTimedOut) {
      return;
    }
    if (job.status !== JobStatus.Succeeded && terminalJobStatuses.includes(job.status)) {
      setErrorMessage((current) => current ?? "The export job failed.");
      setPhase((current) => (current === "running" ? "error" : current));
    }
  }, [job, pollingTimedOut]);

  const isRunning = phase === "running";

  const handleCheckAgain = useCallback(() => {
    setErrorMessage(undefined);
    setPhase("running");
    startPolling(jobId);
  }, [jobId, startPolling]);

  const handleMinimize = useCallback(() => minimize(id), [minimize, id]);
  const handleExpand = useCallback(() => expand(id), [expand, id]);
  const handleClose = useCallback(() => close(id), [close, id]);

  return (
    <MinimizableDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      mode={mode}
      trayIndex={trayIndex}
      // The Waste/Manifests surface opts in to whole-box clickable behavior on
      // the minimized chip (single-click expand instead of hunting for the
      // icon) — same as ManifestUploadDialog / useProcessManifestDocuments.
      wholeBoxClickable
      onMinimizedClick={handleExpand}
      title={
        // minWidth: 0 lets the flex child actually shrink so long titles
        // (e.g. "Manifest PDFs — Jul 2023 – Jul 2026…") truncate with an
        // ellipsis instead of wrapping the title bar; the full text stays
        // available via the native tooltip.
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            <PhaseIndicator phase={phase} />
          </Box>
          <Typography variant="h6" noWrap title={titleForPhase(title, phase, label)}>
            {titleForPhase(title, phase, label)}
          </Typography>
        </Box>
      }
      hideCloseButton
      titleBarActionElement={
        <IconButton
          onClick={(e) => {
            // When the whole minimized box is clickable, the paper-level
            // handler also fires; stop propagation so we don't expand twice.
            if (mode === "minimizedInTray") {
              e.stopPropagation();
              handleExpand();
            } else {
              handleMinimize();
            }
          }}
          // Drop the circular hover affordance when minimized; the whole chip
          // is clickable then, so the icon is a hint, not the primary
          // affordance (mirrors DocumentAIAnalyzingDialog).
          disableRipple={mode === "minimizedInTray"}
          sx={
            mode === "minimizedInTray"
              ? { "&:hover": { backgroundColor: "transparent" } }
              : undefined
          }
        >
          {/* Remove (a centered minus) rather than Minimize: the Minimize
              glyph draws its bar at the bottom of the viewbox, which looks
              off-center inside the circular hover state. */}
          {mode === "minimizedInTray" ? <Maximize /> : <Remove />}
        </IconButton>
      }
      primaryActionButton={primaryActionButton(
        phase,
        handleMinimize,
        handleClose,
        handleCheckAgain
      )}
      hideCancelButton
    >
      <Stack spacing={1.5}>
        {isRunning && (
          <>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary">
              {runningDescription}
            </Typography>
          </>
        )}
        {phase === "success" && (
          <Alert severity="success">
            {notifiesByEmail
              ? "Export ready — downloads opened; links also emailed."
              : "Export ready — your downloads have opened."}
          </Alert>
        )}
        {phase === "timeout" && (
          <Alert severity="info">
            {notifiesByEmail
              ? "Still working — we'll email you the links."
              : "Still working — large exports can take a while. Check again to pick up the finished file."}
          </Alert>
        )}
        {phase === "error" && <Alert severity="error">{errorMessage ?? "Export failed."}</Alert>}
      </Stack>
    </MinimizableDialog>
  );
}
